/**
 * HR sidebar badge store.
 *
 * Holds two groups of counts driving the live menu badges, both written by
 * `<HRBadgeSubscriber />` and read synchronously by each menu item's
 * `countSelector` in `membersMenu.tsx`:
 *
 *  - Admin counts (`pendingLeaves` … `contractsExpiring`) — HRAdmin only,
 *    seeded from REST and kept live by the `hr` dashboards SignalR hub.
 *  - Personal counts (`myPendingLeaves` … `myActiveDuties`) — visible to every
 *    user, REST-baseline only (no HR events exist for them).
 *
 * The two groups have separate writers (`setFromMetrics` / `setPersonalMetrics`);
 * Zustand's `set` merges, so neither write path clobbers the other's fields.
 *
 * Synchronous reads are required by the menu type contract — see
 * [layouts/menu/types.ts](../../layouts/menu/types.ts) `MenuItemCountSelector`.
 */

import { create } from 'zustand';

/** Shape mirrors `HRDashboardMetrics` fields the admin badges care about. */
export interface HrBadgeMetrics {
  pendingLeaveApprovals?: number;
  employeesOnLeaveToday?: number;
  policiesAwaitingSignature?: number;
  contractsExpiring30d?: number;
}

/** Personal "My Records" counts — REST-baseline only, visible to all users. */
export interface HrPersonalBadgeMetrics {
  myPendingLeaves?: number;
  myUnsignedPolicies?: number;
  myActiveDuties?: number;
  /** My leaves with an open request from the approver (duty cover or a question). */
  myLeavesActionNeeded?: number;
}

interface HrBadgesState {
  // Admin counts — HRAdmin only.
  pendingLeaves: number;
  onLeaveToday: number;
  policiesAwaiting: number;
  contractsExpiring: number;
  // Personal counts — all users.
  myPendingLeaves: number;
  myUnsignedPolicies: number;
  myActiveDuties: number;
  myLeavesActionNeeded: number;
  setFromMetrics: (m: HrBadgeMetrics | null | undefined) => void;
  setPersonalMetrics: (m: HrPersonalBadgeMetrics | null | undefined) => void;
  applyDelta: (m: HrBadgeMetrics | null | undefined) => void;
  /** Refreshed by the My Leaves page whenever it loads, so the badge clears after a reply. */
  setMyLeavesActionNeeded: (n: number) => void;
  reset: () => void;
}

const EMPTY = {
  pendingLeaves: 0,
  onLeaveToday: 0,
  policiesAwaiting: 0,
  contractsExpiring: 0,
  myPendingLeaves: 0,
  myUnsignedPolicies: 0,
  myActiveDuties: 0,
  myLeavesActionNeeded: 0,
};

// Counters clamp at zero so a stray -1 delta arriving before the REST
// baseline cannot push the badge negative.
const clamp = (n: number) => (n < 0 ? 0 : n);

export const useHrBadgesStore = create<HrBadgesState>((set) => ({
  ...EMPTY,
  setFromMetrics: (m) =>
    set({
      pendingLeaves: clamp(m?.pendingLeaveApprovals ?? 0),
      onLeaveToday: clamp(m?.employeesOnLeaveToday ?? 0),
      policiesAwaiting: clamp(m?.policiesAwaitingSignature ?? 0),
      contractsExpiring: clamp(m?.contractsExpiring30d ?? 0),
    }),
  setPersonalMetrics: (m) =>
    set({
      myPendingLeaves: clamp(m?.myPendingLeaves ?? 0),
      myUnsignedPolicies: clamp(m?.myUnsignedPolicies ?? 0),
      myActiveDuties: clamp(m?.myActiveDuties ?? 0),
      myLeavesActionNeeded: clamp(m?.myLeavesActionNeeded ?? 0),
    }),
  applyDelta: (m) =>
    set((state) => ({
      pendingLeaves: clamp(state.pendingLeaves + (m?.pendingLeaveApprovals ?? 0)),
      onLeaveToday: clamp(state.onLeaveToday + (m?.employeesOnLeaveToday ?? 0)),
      policiesAwaiting: clamp(state.policiesAwaiting + (m?.policiesAwaitingSignature ?? 0)),
      contractsExpiring: clamp(state.contractsExpiring + (m?.contractsExpiring30d ?? 0)),
    })),
  setMyLeavesActionNeeded: (n) => set({ myLeavesActionNeeded: clamp(n) }),
  reset: () => set(EMPTY),
}));
